// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  LayoutDashboard, Map, ClipboardList, AlertTriangle,
  ShieldCheck, BarChart3, Menu, X, LogOut, User
} from 'lucide-react';

const SUPERVISOR_ROLES = ['supervisor', 'manager', 'owner', 'admin'];

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/zones',     label: 'Zone Map',   icon: Map },
  { path: '/incidents', label: 'Incidents',  icon: AlertTriangle },
];

const SUPERVISOR_NAV = [
  { path: '/supervisor-queue', label: 'Approval Queue', icon: ShieldCheck },
  { path: '/compliance',       label: 'Compliance',     icon: BarChart3 },
];

export default function Layout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSupervisor = SUPERVISOR_ROLES.includes(user?.role);
  const initials = (user?.full_name || user?.email || 'U')
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const allNav = isSupervisor ? [...NAV_ITEMS, ...SUPERVISOR_NAV] : NAV_ITEMS;

  return (
    <div className="app-layout">
      {/* Mobile toggle */}
      <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
        <Menu size={22} />
      </button>

      {/* Sidebar overlay */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div className="sidebar-logo">🧹 AisleSync</div>
              <div className="sidebar-sub">Compliance &amp; Enforcement</div>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              style={{ display: 'none', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
              className="mobile-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section">Operations</div>
          {allNav.slice(0, 3).map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              className={`nav-item ${location.pathname === path ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <Icon size={16} />
              {label}
            </Link>
          ))}

          {isSupervisor && (
            <>
              <div className="sidebar-section" style={{ marginTop: 8 }}>Supervisor</div>
              {SUPERVISOR_NAV.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`nav-item ${location.pathname === path ? 'active' : ''}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <Icon size={16} />
                  {label}
                </Link>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-card" onClick={() => navigate('/dashboard')}>
            <div className="user-avatar">{initials}</div>
            <div>
              <div className="user-name">{user?.full_name || user?.email?.split('@')[0]}</div>
              <div className="user-role">{user?.role || 'janitor'}</div>
            </div>
          </div>
          <button className="sign-out-btn" onClick={handleSignOut}>
            <LogOut size={12} style={{ marginRight: 4 }} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
